import type { CartSummaryType, Nullable } from 'corgi-types';
import type {
  CheckoutType,
  GiftCard,
  Cart as GqlCart,
  UserLoggedInAs,
  UserType,
} from 'graphql-ts-types';

import type { CartVoucherProps } from 'common/components/CartVoucher/types';
import type { IconName } from 'common/components/Icon';
import type { CartPaymentOptionFragment } from 'common/gql/generated/schema';
import type { SubOrder } from 'common/gql/mutation/cart/types';
import type { Device, ServerConfirmDevice } from 'common/types/device';
import type { SubOrderInfo } from 'common/types/shipping';
import type { VoucherCampaign } from './AccordionSection/VoucherSectionSuccess';
import type { DisclaimerInfoDetails } from './DisclaimerInfo';

export type CheckoutInProgressType = Extract<
  CartSummaryType,
  | CartSummaryType.CheckoutShipping
  | CartSummaryType.CheckoutPayment
  | CartSummaryType.CheckoutConfirm
>;

export enum CartSummarySection {
  Discount = 'discount',
  GiftCards = 'giftcards',
}

export interface CartSummarySectionConfig {
  identifier: string;
  iconName: IconName;
  sectionTogglerId: string;
  sectionContentId: string;
}

export type CartOrCheckoutSummaryType = CartSummaryType.Cart | CheckoutInProgressType;

interface GiftCardProps extends Pick<GqlCart, 'giftCardsDeductionTotal'> {
  giftCards: Nullable<Array<Omit<GiftCard, 'validUntil'>>>;
}

export type CartSummaryCart = Pick<
  GqlCart,
  | 'currency'
  | 'subTotal'
  | 'taxTotal'
  | 'amountDue'
  | 'grandTotal'
  | 'preShipping'
  | 'savingsTotal'
  | 'shippingTotal'
  | 'discountTotal'
  | 'ecoMobilierTotal'
  | 'shippingTotalRegular'
  | 'discountPendingTotal'
> &
  GiftCardProps & {
    voucher: CartVoucherProps;
  };

export type CartSummaryDetailBase = {
  cart: CartSummaryCart;
  subOrders: SubOrder[];
  device: ServerConfirmDevice;
  cartZipCode: Nullable<string>;
  articlesCount: number;
  isBlockedForB2B?: boolean;
  checkoutType?: CheckoutType;
  userZipCode?: Nullable<string>;
  loggedInAs?: UserLoggedInAs;
  withHeader?: boolean;
  headerId?: string;
  isBlockedDueToHomeClubCoupon?: boolean;
};

interface CartSummaryDetailCheckout extends CartSummaryDetailBase {
  withDeviceData?: boolean;
  isShippingPageWithAddressesList?: boolean;
  isApplePaySelectedPayment?: boolean;
  applePayConfig?: Nullable<string>;
}

export interface CartSummaryDetail extends CartSummaryDetailCheckout {
  type: CartOrCheckoutSummaryType;
  paypalExpress?: CartPaymentOptionFragment;
}

interface UserProps {
  userType?: UserType;
  loggedInAs: UserLoggedInAs;
  device: Device;
}

interface PaymentProps {
  applePayConfig?: Nullable<string>;
  withPaypalPayLater?: boolean;
  withAdyenPaypalExpress?: boolean;
  isPaymentMethodNotSet?: boolean;
  isApplePaySelectedPayment?: boolean;
  paypalExpress?: CartPaymentOptionFragment;
}

export type CartSummaryProps = {
  user: UserProps;
  cart: CartSummaryCart;
  paymentProps: PaymentProps;
  removeGcSuccessUrl?: string;
  disclaimerInfo?: Nullable<DisclaimerInfoDetails & { showMessage: boolean }>;
  isBlockedForB2B?: boolean;
  checkoutType: CheckoutType;
  type: CartOrCheckoutSummaryType;
  cartZipCode: Nullable<string>;
  userZipCode?: Nullable<string>;
  voucherCampaigns?: VoucherCampaign[];
  subOrders: SubOrderInfo[];
  withLegalTerms?: boolean;
  isShippingPageWithAddressesList?: boolean;
  appleSignInProps?: AppleSignInProps;
  articlesCount: number;
  withDeviceData?: boolean;
  withHeader?: boolean;
  isBlockedDueToHomeClubCoupon?: boolean;
  withShippingCostsBtn?: boolean;
};

export const enum ApplePayButtonType {
  Buy = 'buy',
  SetUp = 'set-up',
}

export enum GiftCardError {
  GiftCardNotActive = 'GiftCardNotActive',
  GiftCardAppDomainNotAllowed = 'GiftCardAppDomainNotAllowed',
  GiftCardNoBalance = 'GiftCardNoBalance',
  GiftCardExpired = 'GiftCardExpired',
  GiftCardInvalidPin = 'GiftCardInvalidPin',
  GiftCardDuplicate = 'GiftCardDuplicate',
  GiftCardNotFound = 'GiftCardNotFound',
  GiftCardInvalidCode = 'GiftCardInvalidCode',
  GiftCardRateLimit = 'RATE_LIMIT_EXCEEDED',
  GiftCardRecaptchaValidation = 'CHALLENGE_VALIDATION_FAILED',
}

export interface AppleSignInProps {
  appleClientId: string;
  appleStateToken: string;
  withAppleFirst: boolean;
}
